import React, { useState, useRef } from "react";
import {
  CloudUpload,
  Description,
  Close,
  CheckCircle,
  Upload as UploadIcon
} from "@mui/icons-material";
import { Button, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { UPLOAD_CONFIG, PROCESS_STEPS } from "../../data/config";
import { FileUtils } from "../../data/utils";
import { DocumentHistoryService } from "../../data/services/document-history.service";
import {
  ClassificationService,
  ClassificationUploadRequest
} from "../../data/services/classification.service";
import { MainRoutes } from "common/config/route.config";
import useProfile from "common/hooks/useProfile";
import CloudCrawler from "../cloud-crawler/CloudCrawler";
import "./Upload.scss";

interface UploadProps {
  onFilesUploaded: (files: File[]) => void;
}

const Upload: React.FC<UploadProps> = ({ onFilesUploaded }) => {
  const navigate = useNavigate();
  const { displayName } = useProfile();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadMode, setUploadMode] = useState<"local" | "cloud">("local");
  const [uploadingDocumentId, setUploadingDocumentId] = useState<string | null>(
    null
  );
  const [currentBatchGuid, setCurrentBatchGuid] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string>("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isLargeUpload, setIsLargeUpload] = useState(false);
  const [uploadSession, setUploadSession] = useState<any>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [canResume, setCanResume] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check for existing upload session on mount
  React.useEffect(() => {
    const savedSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (savedSession) {
      const session = JSON.parse(savedSession);
      setUploadSession(session);
      setCanResume(true);
      setSelectedFiles(session.remainingFiles || []);
      setDocumentTitle(session.title || "");
      setUploadedCount(session.completedCount || 0);
      setTotalCount(session.totalCount || 0);
      setUploadProgress(
        ((session.completedCount || 0) / (session.totalCount || 1)) * 100
      );
    }
  }, []);

  // Constants for batch processing
  const BATCH_SIZE = 10;
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  const LARGE_UPLOAD_THRESHOLD = 100;
  const MAX_CONCURRENT_BATCHES = 2; // Process 2 batches simultaneously
  const SESSION_STORAGE_KEY = "upload_session";

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter((file) =>
      FileUtils.isValidFileType(file, UPLOAD_CONFIG.SUPPORTED_FORMATS)
    );

    if (selectedFiles.length === 0 && validFiles.length > 0) {
      setDocumentTitle("");
      setUploadError(null);
      setUploadProgress(0);
      setUploadedCount(0);
      setTotalCount(0);
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const validFiles = files.filter((file) =>
        FileUtils.isValidFileType(file, UPLOAD_CONFIG.SUPPORTED_FORMATS)
      );

      // If this is a fresh upload (no existing files), clear the classification name
      if (selectedFiles.length === 0 && validFiles.length > 0) {
        setDocumentTitle("");
        setUploadError(null);
        setUploadProgress(0);
        setUploadedCount(0);
        setTotalCount(0);
      }

      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((files) => files.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length > 0 && documentTitle.trim()) {
      const isLarge = selectedFiles.length > LARGE_UPLOAD_THRESHOLD;
      setIsLargeUpload(isLarge);

      if (isLarge) {
        const confirmLarge = window.confirm(
          `You're uploading ${selectedFiles.length} files. This will take significant time and should not be interrupted. Continue?`
        );
        if (!confirmLarge) return;
      }

      setIsUploading(true);
      setUploadError(null);
      setUploadProgress(0);
      setUploadedCount(0);
      setTotalCount(selectedFiles.length);
      setIsPaused(false);

      try {
        const title = documentTitle.trim();
        const allUploadResults: any[] = [];
        let batchGuid: string | null = null;

        // Save session for recovery
        const session = {
          title,
          totalCount: selectedFiles.length,
          remainingFiles: selectedFiles,
          completedCount: 0,
          batchGuid: null,
          startTime: new Date().toISOString()
        };
        setUploadSession(session);
        sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

        // Process files in batches
        for (let i = 0; i < selectedFiles.length; i += BATCH_SIZE) {
          // Check if upload is paused
          if (isPaused) {
            return;
          }

          const batch = selectedFiles.slice(i, i + BATCH_SIZE);
          const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
          const totalBatches = Math.ceil(selectedFiles.length / BATCH_SIZE);

          // Update session
          const updatedSession = {
            ...session,
            currentBatch: batchNumber,
            totalBatches,
            completedCount: allUploadResults.length,
            remainingFiles: selectedFiles.slice(i + BATCH_SIZE)
          };
          setUploadSession(updatedSession);
          sessionStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify(updatedSession)
          );

          let retries = 0;
          let batchSuccess = false;

          while (retries < MAX_RETRIES && !batchSuccess) {
            try {
              const uploadRequest: ClassificationUploadRequest = {
                files: batch,
                title: `${title}_batch_${batchNumber}`,
                classificationName: title,
                metadata: {
                  company: "EY",
                  department: "Technology",
                  createdBy: displayName || "Unknown User",
                  batchNumber: batchNumber,
                  totalBatches: totalBatches,
                  parentBatchGuid: batchGuid
                }
              };

              const response =
                await ClassificationService.uploadForClassificationBatch(
                  uploadRequest
                );

              if (!response.hasErrors && response.value) {
                const batchResults = response.value;
                allUploadResults.push(...batchResults);

                // Extract batchGuid from the first successful batch
                if (
                  !batchGuid &&
                  Array.isArray(batchResults) &&
                  batchResults.length > 0
                ) {
                  batchGuid = batchResults[0].batchGuid || null;
                  setCurrentBatchGuid(batchGuid);
                }

                // Update progress
                setUploadedCount(allUploadResults.length);
                setUploadProgress(
                  (allUploadResults.length / selectedFiles.length) * 100
                );

                batchSuccess = true;
              } else {
                throw new Error(
                  response.errors.join(", ") ||
                    `Batch ${batchNumber} upload failed`
                );
              }
            } catch (batchError: any) {
              retries++;
              console.error(
                `Batch ${batchNumber} attempt ${retries} failed:`,
                batchError
              );

              if (retries >= MAX_RETRIES) {
                throw new Error(
                  `Failed to upload batch ${batchNumber} after ${MAX_RETRIES} attempts: ${batchError.message}`
                );
              } else {
                await new Promise((resolve) =>
                  setTimeout(resolve, RETRY_DELAY)
                );
              }
            }
          }

          // Small delay between batches to avoid overwhelming the server
          if (i + BATCH_SIZE < selectedFiles.length) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }

        // All batches processed successfully
        const allUploaded =
          allUploadResults.length > 0 &&
          allUploadResults.every((result) => result.isUploaded);

        // Create document entry in history with batch processing info
        const newDocument = DocumentHistoryService.addUploadedDocument({
          title: documentTitle.trim(),
          company: "EY",
          department: "Technology",
          createdBy: displayName || "Unknown User",
          createdDate: DocumentHistoryService.getCurrentDate(),
          documentCount: selectedFiles.length,
          status: allUploaded ? "completed" : "processing",
          categories: ["Processing"]
        });

        setUploadingDocumentId(newDocument.id);

        // API returns uploaded status, navigate to classification results
        if (allUploaded && batchGuid) {
          // Navigate directly to CategoriesOverviewPage with batchGuid in URL
          navigate(
            MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
              ":batchGuid",
              batchGuid
            ),
            {
              state: {
                totalFiles: selectedFiles.length,
                classificationName: title
              }
            }
          );
        } else {
          setTimeout(() => {
            DocumentHistoryService.updateDocumentStatus(
              newDocument.id,
              "completed"
            );
            if (batchGuid) {
              // Navigate to classification results after processing
              navigate(
                MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
                  ":batchGuid",
                  batchGuid
                ),
                {
                  state: {
                    totalFiles: selectedFiles.length,
                    classificationName: title
                  }
                }
              );
            }
          }, 3000);
        }
      } catch (error: any) {
        console.error("Upload error:", error);
        setUploadError(
          error.message || "Failed to upload documents. Please try again."
        );
      } finally {
        setIsUploading(false);
      }
    }
  };

  // for classification status updates
  const pollClassificationStatus = async (
    apiDocumentId: string,
    localDocumentId: string
  ) => {
    // Validate that we have a valid document GUID
    if (!apiDocumentId || apiDocumentId === "undefined") {
      console.error("Invalid document GUID for status polling:", apiDocumentId);
      // Fall back to navigating to classification results after timeout
      setTimeout(() => {
        DocumentHistoryService.updateDocumentStatus(
          localDocumentId,
          "completed"
        );
        if (currentBatchGuid) {
          navigate(
            MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
              ":batchGuid",
              currentBatchGuid
            ),
            {
              state: {
                totalFiles: selectedFiles.length,
                classificationName: documentTitle
              }
            }
          );
        }
      }, 2000);
      return;
    }

    try {
      const statusResponse =
        await ClassificationService.getClassificationStatus(apiDocumentId);

      if (!statusResponse.hasErrors && statusResponse.value) {
        const { status } = statusResponse.value;

        if (status === "completed") {
          DocumentHistoryService.updateDocumentStatus(
            localDocumentId,
            "completed"
          );
          if (currentBatchGuid) {
            navigate(
              MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
                ":batchGuid",
                currentBatchGuid
              ),
              {
                state: {
                  totalFiles: selectedFiles.length,
                  classificationName: documentTitle
                }
              }
            );
          }
        } else if (status === "failed") {
          DocumentHistoryService.updateDocumentStatus(
            localDocumentId,
            "failed"
          );
          setUploadError("Document classification failed. Please try again.");
        } else {
          // polling if still processing
          setTimeout(
            () => pollClassificationStatus(apiDocumentId, localDocumentId),
            2000
          );
        }
      } else {
        console.error("Failed to get classification status");
        setUploadError("Document classification failed. Please try again.");
      }
    } catch (error) {
      console.error("Status polling error:", error);
      // Fall back to navigating to classification results after timeout
      setTimeout(() => {
        DocumentHistoryService.updateDocumentStatus(
          localDocumentId,
          "completed"
        );
        if (currentBatchGuid) {
          navigate(
            MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
              ":batchGuid",
              currentBatchGuid
            ),
            {
              state: {
                totalFiles: selectedFiles.length,
                classificationName: documentTitle
              }
            }
          );
        }
      }, 5000);
    }
  };

  const onUploadComplete = async (
    files: File[],
    cloudClassificationName?: string
  ) => {
    setIsUploading(true);
    setUploadError(null);

    try {
      // Use cloud classification name if provided, otherwise use the input field value
      const title = cloudClassificationName || documentTitle.trim();

      // Prepare the upload request for cloud files
      const uploadRequest: ClassificationUploadRequest = {
        files: files,
        title: title,
        classificationName: title,
        metadata: {
          company: "EY",
          department: "Technology",
          createdBy: displayName || "Unknown User"
        }
      };

      // Call the real API for cloud uploads
      const response = await ClassificationService.uploadForClassification(
        uploadRequest
      );

      if (!response.hasErrors && response.value) {
        const uploadResults = response.value;

        // Extract batchGuid from the first result (all documents in batch share same batchGuid)
        let batchGuid: string | null = null;
        if (Array.isArray(uploadResults) && uploadResults.length > 0) {
          batchGuid = uploadResults[0].batchGuid || null;
          setCurrentBatchGuid(batchGuid);
        }

        // Check if all documents are uploaded
        const allUploaded =
          Array.isArray(uploadResults) &&
          uploadResults.length > 0 &&
          uploadResults.every((result) => result.isUploaded);

        // For cloud uploads, also add to document history
        const newDocument = DocumentHistoryService.addUploadedDocument({
          title: title,
          company: "EY",
          department: "Technology",
          createdBy: displayName || "Unknown User",
          createdDate: DocumentHistoryService.getCurrentDate(),
          documentCount: files.length,
          status: allUploaded ? "completed" : "processing",
          categories: ["Processing"]
        });

        setUploadingDocumentId(newDocument.id);

        // Handle classification completion - navigate to results
        if (allUploaded && batchGuid) {
          // Navigate directly to CategoriesOverviewPage with batchGuid in URL
          navigate(
            MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
              ":batchGuid",
              batchGuid
            ),
            {
              state: {
                totalFiles: files.length,
                classificationName: title
              }
            }
          );
        } else {
          // For cloud uploads with multiple files, navigate after delay
          setTimeout(() => {
            DocumentHistoryService.updateDocumentStatus(
              newDocument.id,
              "completed"
            );
            if (batchGuid) {
              navigate(
                MainRoutes.DATA_CLASSIFICATION_RESULTS.replace(
                  ":batchGuid",
                  batchGuid
                ),
                {
                  state: {
                    totalFiles: files.length,
                    classificationName: title
                  }
                }
              );
            }
          }, 3000);
        }
      } else {
        throw new Error(response.errors.join(", ") || "Cloud upload failed");
      }
    } catch (error: any) {
      console.error("Cloud upload error:", error);
      setUploadError(
        error.message || "Failed to upload cloud documents. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleNewUpload = () => {
    setSelectedFiles([]);
    setUploadMode("local");
    setUploadingDocumentId(null);
    setCurrentBatchGuid(null);
    setDocumentTitle(""); // Clear classification name
    setUploadError(null);
    setUploadProgress(0);
    setUploadedCount(0);
    setTotalCount(0);
    setIsLargeUpload(false);
    setIsPaused(false);

    // Clear any existing upload session
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setUploadSession(null);
    setCanResume(false);
  };

  const formatDisplayFormats = () => {
    return UPLOAD_CONFIG.SUPPORTED_FORMATS.map((format: string) =>
      format.replace(".", "").toUpperCase()
    ).slice(0, 5);
  };

  return (
    <div className="classification-upload">
      {/* Header Section */}
      <div className="upload-header">
        <div className="header-content">
          <h2>Upload Your Documents</h2>
          <p>Choose your documents and let us organize them automatically</p>
        </div>

        {/* Process Steps */}
        <div className="process-steps">
          {PROCESS_STEPS.map((step: any, index: number) => {
            const IconComponent = getIconComponent(step.icon);
            return (
              <div key={index} className="process-step">
                <div className="step-indicator">
                  <div
                    className={`step-number ${step.isActive ? "active" : ""}`}
                    style={{ borderColor: step.color }}
                  >
                    {step.step}
                  </div>
                  {index < PROCESS_STEPS.length - 1 && (
                    <div className="step-connector"></div>
                  )}
                </div>
                <div className="step-content">
                  <div
                    className="step-icon-wrapper"
                    style={{ backgroundColor: `${step.color}20` }}
                  >
                    <IconComponent
                      className="step-icon"
                      style={{ color: step.color }}
                    />
                  </div>
                  <h4 className="step-title">{step.title}</h4>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Section */}
      <div className="upload-content">
        <div className="upload-tabs">
          <div className="tab-list">
            <button
              className={`tab-trigger ${
                uploadMode === "local" ? "active" : ""
              }`}
              onClick={() => setUploadMode("local")}
            >
              <UploadIcon className="tab-icon" />
              Local Files
            </button>
            <button
              className={`tab-trigger ${
                uploadMode === "cloud" ? "active" : ""
              }`}
              onClick={() => setUploadMode("cloud")}
            >
              <CloudUpload className="tab-icon" />
              Cloud Storage
            </button>
          </div>

          {uploadMode === "local" && (
            <div className="tab-content">
              <div className="upload-container">
                <div
                  className={`drop-zone ${dragActive ? "drag-active" : ""}`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="drop-zone-content">
                    <div
                      className={`upload-icon-container ${
                        dragActive ? "drag-active" : ""
                      }`}
                    >
                      <div className="upload-icon-wrapper">
                        <UploadIcon
                          className="upload-icon"
                          onClick={() => inputRef.current?.click()}
                        />
                      </div>
                    </div>
                    <div className="upload-text">
                      <p className="primary-text">
                        {dragActive ? (
                          "Drop files here to upload"
                        ) : (
                          <>
                            Drag and drop files here, or{" "}
                            <button
                              type="button"
                              onClick={() => inputRef.current?.click()}
                              className="browse-button"
                            >
                              browse
                            </button>
                          </>
                        )}
                      </p>
                      <div className="supported-formats">
                        <span>Supports</span>
                        <div className="format-tags">
                          {formatDisplayFormats().map((format: string) => (
                            <span key={format} className="format-tag">
                              {format}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <input
                      ref={inputRef}
                      type="file"
                      multiple
                      onChange={handleChange}
                      className="file-input"
                      accept={UPLOAD_CONFIG.SUPPORTED_FORMATS.join(",")}
                    />
                  </div>
                </div>

                {selectedFiles.length > 0 && (
                  <div className="selected-files">
                    <div className="files-header">
                      <CheckCircle className="check-icon" />
                      <h3>Selected Files ({selectedFiles.length})</h3>
                    </div>

                    {/* Classification Name Input */}
                    <div className="document-title-section">
                      <label className="title-label">
                        Classification Name{" "}
                        <span className="required-asterisk">*</span>
                      </label>
                      <input
                        type="text"
                        className="title-input"
                        placeholder="Enter a classification name"
                        value={documentTitle}
                        onChange={(e) => setDocumentTitle(e.target.value)}
                        maxLength={50}
                        required
                      />
                      <p className="title-hint">
                        Please provide a name for classification of your
                        documents
                      </p>
                    </div>

                    <div className="files-list">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="file-item">
                          <div className="file-info">
                            <div className="file-icon">
                              <Description />
                            </div>
                            <div className="file-details">
                              <p className="file-name">{file.name}</p>
                              <p className="file-size">
                                {FileUtils.formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="remove-button"
                          >
                            <Close />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="upload-actions">
                      {uploadError && (
                        <div className="error-message">{uploadError}</div>
                      )}

                      {isUploading && totalCount > 20 && (
                        <div className="upload-progress">
                          <div className="progress-info">
                            <p>
                              Processing {uploadedCount} of {totalCount}{" "}
                              files...
                              {/* (
                              {Math.ceil(selectedFiles.length / BATCH_SIZE)}{" "}
                              batches) */}
                            </p>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <p className="progress-text">
                              {Math.round(uploadProgress)}% completed
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="action-buttons">
                        {selectedFiles.length > 0 && !isUploading && (
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={handleNewUpload}
                            className="clear-button"
                            startIcon={<Close />}
                          >
                            Clear All
                          </Button>
                        )}

                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleUpload}
                          className="upload-button"
                          startIcon={
                            isUploading ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <CheckCircle />
                            )
                          }
                          disabled={!documentTitle.trim() || isUploading}
                        >
                          {isUploading
                            ? // `Uploading batch ${Math.ceil(
                              //     uploadedCount / BATCH_SIZE
                              //   )}...`
                              `Uploading...`
                            : `Classify ${selectedFiles.length} ${
                                selectedFiles.length === 1 ? "File" : "Files"
                              }`}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {uploadMode === "cloud" && (
            <div className="tab-content">
              <CloudCrawler
                onComplete={onUploadComplete}
                onCancel={() => setUploadMode("local")}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function getIconComponent(iconName: string) {
  switch (iconName) {
    case "Upload":
      return UploadIcon;
    case "CheckCircle":
      return CheckCircle;
    case "FileText":
      return Description;
    default:
      return UploadIcon;
  }
}

export default Upload;
